/* Native phone-model authorization. This is a separate state from the display variant. */
(function (root, factory) {
  'use strict';
  if (typeof module === 'object' && module.exports) module.exports = factory(null);
  else if (!root.ShamosModelChoice) root.ShamosModelChoice = factory(root);
})(typeof window === 'undefined' ? null : window, function (win) {
  'use strict';
  var controllers = new WeakMap();
  var pickers = new WeakMap();
  var PLACEHOLDER = 'Choose your phone model';
  var FORM = 'form[data-model-choice-form], form[data-type="add-to-cart-form"], form.product-info__form, form.sticky-add-to-cart__form';
  var STATUS = {
    unset: 'Choose your phone model before adding to cart.',
    pending: 'Checking your selected phone model…',
    error: 'We could not confirm that phone model. Please choose it again.'
  };
  function id(value) { return value == null ? '' : String(value); }
  function positiveId(value) { return /^[1-9][0-9]*$/.test(id(value)); }
  function uniqueMatch(options, predicate) {
    var matches = options.filter(predicate);
    return matches.length === 1 ? matches[0] : null;
  }
  function positions(options) {
    return Array.from(new Set(options.map(function (option) { return option.position; }))).sort();
  }
  function completeValues(values, options) {
    var ps = positions(options);
    if (!Array.isArray(values) || values.length !== ps.length || ps.some(function (p, i) { return p !== i + 1; })) return null;
    var selected = values.map(function (value, i) {
      return uniqueMatch(options, function (option) { return option.position === i + 1 && option.id === id(value); });
    });
    return selected.every(Boolean) ? selected : null;
  }
  function variantMatches(variant, wanted, config, options) {
    if (!variant || !positiveId(variant.id) || !wanted || !Array.isArray(variant.options)) return false;
    if (variant.product_id != null && id(variant.product_id) !== id(config.productId)) return false;
    if (variant.options.length !== positions(options).length || variant.options[config.modelPosition - 1] !== wanted.model) return false;
    return Boolean(uniqueMatch(options, function (option) {
      return option.position === config.modelPosition && option.id === wanted.optionId && option.value === wanted.model;
    }));
  }
  function intended(option, source, values) {
    return { model: option.value, optionId: option.id, source: source, values: values || null };
  }
  function initialDecision(config, options, variant, href, memory) {
    var url;
    try { url = new URL(href); } catch (_) { return { error: STATUS.error }; }
    var modelOptions = options.filter(function (option) { return option.position === config.modelPosition; });
    function variantModel() {
      if (!variant || !Array.isArray(variant.options)) return null;
      return uniqueMatch(modelOptions, function (option) { return option.value === variant.options[config.modelPosition - 1]; });
    }
    // Presence, including blank/duplicate/foreign IDs, blocks all lower-priority fallbacks.
    if (url.searchParams.has('variant')) {
      var variants = url.searchParams.getAll('variant');
      var incoming = variants[0];
      var model = variantModel();
      var wanted = model && intended(model, 'variant');
      if (variants.length === 1 && positiveId(incoming) && incoming === id(config.explicitVariantId) && incoming === id(variant && variant.id) && variantMatches(variant, wanted, config, options)) {
        return { wanted: wanted, ready: true };
      }
      return { error: 'This product link does not identify a valid phone model. Please choose yours.' };
    }
    if (url.searchParams.has('option_values')) {
      var incomingValues = url.searchParams.getAll('option_values');
      var values = incomingValues[0].split(',');
      var selected = incomingValues.length === 1 && values.every(positiveId) && completeValues(values, options);
      var optionWanted = selected && intended(selected[config.modelPosition - 1], 'option_values', values);
      if (selected && positiveId(config.explicitVariantId) && id(config.explicitVariantId) === id(variant && variant.id) && variantMatches(variant, optionWanted, config, options) && selected.every(function (option, i) { return option.value === variant.options[i]; })) {
        return { wanted: optionWanted, ready: true };
      }
      return { error: 'This product link has an incomplete or unavailable selection. Please choose your phone model.' };
    }
    // Only opt-in sibling links request a fresh choice; explicit selections above still win.
    if (url.searchParams.get('shamos_model_choice') === 'required') {
      return { wanted: null, needsResolve: false };
    }
    if (config.unresolvedCollectionContext === true) {
      return { error: 'Please choose the exact phone model for this design.' };
    }
    if (config.collectionModels.length) {
      var collectionModel = uniqueMatch(modelOptions, function (option) { return config.collectionModels.indexOf(option.value) !== -1; });
      if (!collectionModel) return { error: 'This design does not offer one exact match for the requested phone model. Please choose from the models shown.' };
      return { wanted: intended(collectionModel, 'collection'), needsResolve: true };
    }
    if (memory && memory.version === 1 && typeof memory.model === 'string' && (memory.source === 'manual' || memory.source === 'collection')) {
      var remembered = uniqueMatch(modelOptions, function (option) { return option.value === memory.model; });
      if (remembered) {
        var rememberedValues = id(memory.productId) === id(config.productId) && completeValues(memory.optionValueIds, options);
        // Never carry another product's variant or option-value IDs into this picker.
        var safeValues = rememberedValues && rememberedValues[config.modelPosition - 1].value === remembered.value ? memory.optionValueIds.map(id) : null;
        var restored = intended(remembered, 'memory', safeValues);
        restored.rememberSource = memory.source;
        return { wanted: restored, needsResolve: true };
      }
    }
    return { wanted: null, needsResolve: false };
  }
  function findScope(element) {
    return element && element.closest ? element.closest('[data-model-choice-state]') : null;
  }
  function isOwnProduct(element, scope) {
    var marked = element && element.closest && element.closest('[data-model-choice-form-product]');
    var product = scope && (scope.dataset.modelChoiceProduct || scope.dataset.modelChoiceProductId);
    return !marked || !product || id(marked.dataset.modelChoiceFormProduct) === id(product);
  }
  function readOptions(picker) {
    return Array.from(picker.querySelectorAll('.variant-option')).map(function (element) {
      return { id: id(element.dataset.optionValueId), value: element.dataset.value, position: Number(element.dataset.optionPosition), element: element };
    });
  }
  function configuration(picker) {
    var script = picker.querySelector('script[data-model-choice-config]');
    if (!script) return null;
    try {
      var config = JSON.parse(script.textContent);
      config.productId = id(config.productId);
      config.modelPosition = Number(config.modelPosition);
      if (!positiveId(config.productId) || config.productId !== id(picker.dataset.productId) || !Number.isInteger(config.modelPosition) || config.modelPosition < 1 || config.modelPosition > 3 || !Array.isArray(config.collectionModels) || config.collectionModels.some(function (value) { return typeof value !== 'string' || !value; })) return null;
      config.explicitVariantId = id(config.explicitVariantId);
      return config;
    } catch (_) { return null; }
  }
  function Controller(picker, config, scope) {
    this.picker = picker;
    this.config = config;
    this.scope = scope;
    this.state = 'pending';
    this.wanted = null;
    this.committedVariantId = '';
    this.error = '';
    this.initialized = false;
    this.destroyed = false;
    this.rememberPending = false;
    this.lastURL = win.location.href;
    this.storageKey = config.themeId != null && positiveId(config.themeId) ? 'shamos:model-choice:v1:theme:' + id(config.themeId) : null;
    this.dynamicOriginal = new WeakMap();
    this.displayModelOptionId = id((picker.optionsSelectedValues || [])[config.modelPosition - 1]);
    if (!this.displayModelOptionId && picker.currentVariant && Array.isArray(picker.currentVariant.options)) {
      var displayOption = uniqueMatch(this.options(), function (option) { return option.position === config.modelPosition && option.value === picker.currentVariant.options[config.modelPosition - 1]; });
      this.displayModelOptionId = displayOption ? displayOption.id : '';
    }
    this.onSubmit = this.handleSubmit.bind(this);
    this.onFocusClick = this.handleFocusClick.bind(this);
    this.onHistory = this.restore.bind(this);
    this.scope.addEventListener('submit', this.onSubmit, true);
    this.scope.addEventListener('click', this.onFocusClick);
    win.addEventListener('popstate', this.onHistory);
    win.addEventListener('pageshow', this.onHistory);
  }
  Controller.prototype.options = function () { return readOptions(this.picker); };
  Controller.prototype.snapshot = function () {
    return { state: this.state, ready: this.state === 'ready', productId: this.config.productId, variantId: this.committedVariantId, model: this.wanted ? this.wanted.model : '', source: this.wanted ? this.wanted.source : '', error: this.error };
  };
  Controller.prototype.readMemory = function () {
    if (!this.storageKey) return null;
    try { return JSON.parse(win.sessionStorage.getItem(this.storageKey)); } catch (_) { return null; }
  };
  Controller.prototype.remember = function () {
    var source = this.wanted && (this.wanted.rememberSource || this.wanted.source);
    if (!this.storageKey || !this.rememberPending || !this.wanted || (source !== 'manual' && source !== 'collection')) return;
    try {
      var variant = this.picker.currentVariant;
      var options = this.options();
      var optionIds = variant.options.map(function (value, i) {
        var match = uniqueMatch(options, function (option) { return option.position === i + 1 && option.value === value; });
        return match ? match.id : '';
      });
      win.sessionStorage.setItem(this.storageKey, JSON.stringify({ version: 1, model: this.wanted.model, source: source, productId: this.config.productId, optionValueIds: optionIds }));
    } catch (_) { /* Storage denial is not a model-selection failure. */ }
    this.rememberPending = false;
  };
  Controller.prototype.initialize = function () {
    if (this.initialized) { this.sync(); return false; }
    this.initialized = true;
    var decision = initialDecision(this.config, this.options(), this.picker.currentVariant, win.location.href, this.readMemory());
    this.wanted = decision.wanted || null;
    this.error = decision.error || '';
    this.rememberPending = Boolean(this.wanted && this.wanted.source === 'collection');
    if (this.wanted && this.wanted.values) this.applyValues(this.wanted.values);
    if (decision.ready) this.accept(this.picker.currentVariant);
    else { this.state = this.error ? 'error' : this.wanted ? 'pending' : 'unset'; this.sync(); }
    return Boolean(decision.needsResolve);
  };
  Controller.prototype.applyValues = function (values) {
    var selected = completeValues(values, this.options());
    if (!selected) return false;
    this.picker.optionsSelectedValues = values.map(id);
    var options = this.options();
    options.forEach(function (option) {
      var chosen = selected[option.position - 1].id === option.id;
      option.element.classList.toggle('selected', chosen);
      option.element.setAttribute('aria-selected', String(chosen));
      if (option.element.matches('input')) option.element.checked = chosen;
    });
    return true;
  };
  Controller.prototype.choose = function (targetOption) {
    var options = this.options();
    var chosen = options.find(function (option) { return option.element === targetOption; });
    if (!chosen) return false;
    if (chosen.position === this.config.modelPosition) {
      if (!uniqueMatch(options, function (option) { return option.position === chosen.position && option.value === chosen.value; })) return this.fail('That phone model is ambiguous. Please choose another model.');
      this.wanted = intended(chosen, 'manual');
      this.rememberPending = true;
    } else if (this.wanted && this.state === 'ready') {
      // A finish edit can update a verified model choice, never create one.
      this.rememberPending = true;
      if (this.wanted.source === 'variant' || this.wanted.source === 'option_values') this.wanted.rememberSource = 'manual';
    }
    // A finish-only click never manufactures model intent.
    this.begin();
    return true;
  };
  Controller.prototype.chooseValues = function (values) {
    // Unready sticky actions route to the main chooser, not a first-model fallback.
    if (!this.wanted || this.state !== 'ready') { focusChoice(this.scope); return false; }
    var selected = completeValues(values, this.options());
    if (!selected) return this.fail();
    var chosen = selected[this.config.modelPosition - 1];
    if (chosen.id !== this.wanted.optionId || chosen.value !== this.wanted.model) {
      this.wanted = intended(chosen, 'manual');
    } else {
      // A sticky finish-only edit retains the established model provenance.
      this.wanted.values = values.map(id);
      if (this.wanted.source === 'variant' || this.wanted.source === 'option_values') this.wanted.rememberSource = 'manual';
    }
    this.rememberPending = true;
    this.begin();
    this.applyValues(values);
    return true;
  };
  Controller.prototype.begin = function () {
    if (this.destroyed) return false;
    this.committedVariantId = '';
    this.error = '';
    this.state = this.wanted ? 'pending' : 'unset';
    this.sync();
    return Boolean(this.wanted);
  };
  Controller.prototype.accept = function (variant) {
    if (this.destroyed) return false;
    if (!this.wanted) { this.state = 'unset'; this.committedVariantId = ''; this.sync(); return false; }
    if (id(this.picker.dataset.productId) !== this.config.productId || !variantMatches(variant, this.wanted, this.config, this.options()) || !variantMatches(this.picker.currentVariant, this.wanted, this.config, this.options()) || id(variant.id) !== id(this.picker.currentVariant && this.picker.currentVariant.id)) return this.fail();
    this.state = 'ready';
    this.error = '';
    this.committedVariantId = id(variant.id);
    this.remember();
    this.sync();
    return true;
  };
  Controller.prototype.fail = function (message) {
    this.state = 'error';
    this.error = typeof message === 'string' && message ? message : STATUS.error;
    this.committedVariantId = '';
    this.sync();
    return false;
  };
  Controller.prototype.setModelPresentation = function (scope) {
    var self = this;
    var position = this.config.modelPosition;
    var modelOptions = Array.from(scope.querySelectorAll('.variant-option')).filter(function (element) { return Number(element.dataset.optionPosition) === position; });
    var selected = this.wanted ? this.wanted.optionId : '';
    modelOptions.forEach(function (element) {
      var chosen = id(element.dataset.optionValueId) === selected;
      element.classList.toggle('selected', chosen);
      element.setAttribute('aria-selected', String(chosen));
      if (element.matches('input')) element.checked = chosen;
      Array.from(scope.querySelectorAll('.variant-option__label')).filter(function (label) { return id(label.dataset.optionValueId) === id(element.dataset.optionValueId); }).forEach(function (label) { label.classList.toggle('selected', chosen); });
    });
    var label = this.wanted ? this.wanted.model : PLACEHOLDER;
    scope.querySelectorAll('.variant-option__selected-value, .variant-option__select-button__label').forEach(function (element) {
      if (Number(element.dataset.position || element.dataset.optionPosition) === position) element.textContent = label;
    });
    scope.querySelectorAll('select-element').forEach(function (element) {
      if (Number(element.dataset.optionPosition) !== position) return;
      var input = element.querySelector('.select-option-selected');
      if (input) input.value = self.wanted ? self.wanted.model : '';
      var button = element.querySelector('.select-button');
      if (button) button.setAttribute('aria-label', 'Phone model: ' + label);
    });
  };
  Controller.prototype.showUnresolvedOptions = function (scope) {
    var originals = this.picker.constrainedOptionPresentation;
    scope.querySelectorAll('[data-variant-nonexistent="true"]').forEach(function (element) {
      var original = originals && originals.get(element);
      element.hidden = false;
      element.removeAttribute('inert');
      element.removeAttribute('aria-hidden');
      element.dataset.variantNonexistent = 'false';
      if (original && original.tabindex !== null) element.setAttribute('tabindex', original.tabindex);
      else if (original) element.removeAttribute('tabindex');
      if (original && original.display) element.style.setProperty('display', original.display, original.displayPriority);
      else element.style.removeProperty('display');
    });
  };
  Controller.prototype.sync = function () {
    if (this.destroyed) return;
    var self = this;
    var ready = this.state === 'ready';
    // Keep checkout CSS gated until all real form IDs below are synchronized.
    if (!ready) this.scope.dataset.modelChoiceState = this.state;
    this.scope.dataset.modelChoiceProduct = this.config.productId;
    this.scope.dataset.modelChoiceProductId = this.config.productId;
    this.scope.dataset.modelChoiceVariantId = ready ? this.committedVariantId : '';
    this.picker.dataset.modelChoiceReady = String(ready);
    // Never mutate currentVariant, data-current-variant-id, catalog data, or media.
    var optionValues = (this.picker.optionsSelectedValues || []).slice();
    // Native requests may preview a real display model. This ID is never authorization.
    optionValues[this.config.modelPosition - 1] = this.wanted ? this.wanted.optionId : this.displayModelOptionId;
    this.picker.optionsSelectedValues = optionValues;
    this.setModelPresentation(this.picker);
    var sticky = this.scope.querySelector('[id^="StickyAddToCart__Variant__"]');
    if (sticky) this.setModelPresentation(sticky);
    if (!ready) { this.showUnresolvedOptions(this.picker); if (sticky) this.showUnresolvedOptions(sticky); }
    this.scope.querySelectorAll(FORM).forEach(function (form) {
      if (!isOwnProduct(form, self.scope)) return;
      form.querySelectorAll('input[name="id"]').forEach(function (input) {
        // Commit the authoritative ID before enabling any own field or exposing
        // accelerated checkout, including before Alpine has hydrated this form.
        if (ready) input.value = self.committedVariantId;
        // Availability is independent of model authorization; sold-out stays disabled.
        input.disabled = !ready || !Boolean(self.picker.currentVariant && self.picker.currentVariant.available);
      });
    });
    if (ready) this.scope.dataset.modelChoiceState = 'ready';
    this.scope.querySelectorAll('.product-form__dynamic-checkout, [data-model-choice-dynamic], [data-model-choice-checkout]').forEach(function (element) {
      if (!isOwnProduct(element, self.scope)) return;
      if (!self.dynamicOriginal.has(element)) self.dynamicOriginal.set(element, { inert: element.hasAttribute('inert') && !element.hasAttribute('data-model-choice-checkout') && !element.hasAttribute('data-model-choice-dynamic'), ariaHidden: element.getAttribute('aria-hidden') });
      var original = self.dynamicOriginal.get(element);
      if (!ready) { element.setAttribute('inert', ''); element.setAttribute('aria-hidden', 'true'); }
      else {
        if (!original.inert) element.removeAttribute('inert');
        if (original.ariaHidden === null) element.removeAttribute('aria-hidden'); else element.setAttribute('aria-hidden', original.ariaHidden);
      }
    });
    var status = this.picker.querySelector('[data-model-choice-status]');
    if (!status) {
      status = this.picker.ownerDocument.createElement('p');
      status.setAttribute('data-model-choice-status', '');
      status.setAttribute('role', 'status');
      status.setAttribute('aria-live', 'polite');
      status.setAttribute('aria-atomic', 'true');
      this.picker.appendChild(status);
    }
    status.textContent = ready ? '' : this.error || STATUS[this.state] || STATUS.unset;
    status.hidden = ready;
    this.scope.querySelectorAll('[data-model-choice-price-range]').forEach(function (element) {
      if (typeof self.config.productPriceRange === 'string') element.textContent = self.config.productPriceRange;
    });
    this.scope.dispatchEvent(new win.CustomEvent('shamos:model-choice-change', { bubbles: true, detail: this.snapshot() }));
  };
  Controller.prototype.updateURL = function () {
    if (this.state !== 'ready' || !(this.picker.isMainProduct || this.picker.dataset.mainProduct === 'true') || !canSubmit(this.picker, this.committedVariantId)) return false;
    try {
      var url = new URL(win.location.href);
      url.searchParams.set('variant', this.committedVariantId);
      url.searchParams.delete('option_values');
      win.history.replaceState(win.history.state, '', url.href);
      this.lastURL = url.href;
      return url.href;
    } catch (_) { return false; }
  };
  Controller.prototype.restore = function () {
    if (this.destroyed) return;
    if (win.location.href === this.lastURL) {
      if (this.state === 'ready' && !canSubmit(this.picker, this.committedVariantId)) this.fail();
      else this.sync();
      return;
    }
    // BFCache of this document is safe. A different history entry is not evidence
    // that this document's current variant belongs to that entry; do not guess it.
    this.lastURL = win.location.href;
    var url;
    try { url = new URL(win.location.href); } catch (_) { return this.fail(); }
    if (url.searchParams.getAll('variant').length === 1 && url.searchParams.get('variant') === this.committedVariantId && canSubmit(this.picker, this.committedVariantId)) return this.sync();
    this.wanted = null;
    this.rememberPending = false;
    this.fail('The product link changed. Please choose your phone model to confirm it.');
  };
  Controller.prototype.handleSubmit = function (event) {
    var form = event.target;
    if (!form || !form.matches(FORM)) return;
    var input = form.querySelector('input[name="id"]');
    if (!canSubmit(form, input && input.value)) {
      event.preventDefault();
      event.stopImmediatePropagation();
      focusChoice(form);
    }
  };
  Controller.prototype.handleFocusClick = function (event) {
    var trigger = event.target.closest && event.target.closest('[data-model-choice-focus]');
    if (trigger && this.scope.contains(trigger)) { event.preventDefault(); focusChoice(trigger); }
  };
  Controller.prototype.destroy = function () {
    this.scope.removeEventListener('submit', this.onSubmit, true);
    this.scope.removeEventListener('click', this.onFocusClick);
    win.removeEventListener('popstate', this.onHistory);
    win.removeEventListener('pageshow', this.onHistory);
    if (controllers.get(this.scope) === this) controllers.delete(this.scope);
    this.destroyed = true;
    this.scope.dataset.modelChoiceState = 'pending';
  };
  function canSubmit(element, variantId) {
    var scope = findScope(element);
    if (!scope || !isOwnProduct(element, scope)) return true;
    var controller = controllers.get(scope);
    return Boolean(controller && !controller.destroyed && id(controller.picker.dataset.productId) === controller.config.productId && id(scope.dataset.modelChoiceProduct || scope.dataset.modelChoiceProductId) === controller.config.productId && controller.state === 'ready' && positiveId(variantId) && id(variantId) === controller.committedVariantId && id(controller.picker.currentVariant && controller.picker.currentVariant.id) === controller.committedVariantId && variantMatches(controller.picker.currentVariant, controller.wanted, controller.config, controller.options()));
  }
  function focusChoice(element) {
    var scope = findScope(element);
    var controller = scope && controllers.get(scope);
    if (!controller || !isOwnProduct(element, scope)) return false;
    var picker = controller.picker;
    var position = controller.config.modelPosition;
    var dropdown = Array.from(picker.querySelectorAll('select-element')).find(function (el) { return Number(el.dataset.optionPosition) === position; });
    var focusTarget = dropdown && dropdown.querySelector('.select-button');
    if (!focusTarget) {
      var model = controller.options().find(function (option) { return option.position === position && (!controller.wanted || option.id === controller.wanted.optionId); });
      if (model) focusTarget = Array.from(picker.querySelectorAll('.variant-option__label')).find(function (label) { return id(label.dataset.optionValueId) === model.id; }) || model.element;
    }
    if (!focusTarget) focusTarget = picker;
    if (focusTarget === picker && !picker.hasAttribute('tabindex')) picker.setAttribute('tabindex', '-1');
    try { focusTarget.focus({ preventScroll: true }); } catch (_) { focusTarget.focus(); }
    if (picker.scrollIntoView) picker.scrollIntoView({ behavior: 'auto', block: 'center' });
    return true;
  }
  var api = {
    attach: function (picker) {
      if (!win || !picker) return null;
      var existing = pickers.get(picker);
      if (existing && !existing.destroyed) return existing;
      if (existing && existing.destroyed && findScope(picker) === existing.scope) {
        var current = controllers.get(existing.scope);
        if (current && current !== existing) current.destroy();
        existing.destroyed = false;
        controllers.set(existing.scope, existing);
        existing.scope.addEventListener('submit', existing.onSubmit, true);
        existing.scope.addEventListener('click', existing.onFocusClick);
        win.addEventListener('popstate', existing.onHistory);
        win.addEventListener('pageshow', existing.onHistory);
        existing.restore();
        return existing;
      }
      var scope = findScope(picker);
      var config = configuration(picker);
      if (!scope || !config || ((scope.dataset.modelChoiceProduct || scope.dataset.modelChoiceProductId) && id(scope.dataset.modelChoiceProduct || scope.dataset.modelChoiceProductId) !== config.productId)) return null;
      var previous = controllers.get(scope);
      if (previous) previous.destroy();
      var controller = new Controller(picker, config, scope);
      controllers.set(scope, controller);
      pickers.set(picker, controller);
      return controller;
    },
    canSubmit: canSubmit,
    focusChoice: focusChoice,
    getState: function (element) { var scope = findScope(element); var controller = scope && isOwnProduct(element, scope) && controllers.get(scope); return controller ? controller.snapshot() : null; }
  };
  // Pure helpers are exported only in Node for dependency-free unit tests.
  if (!win) api.testing = { initialDecision: initialDecision, completeValues: completeValues, variantMatches: variantMatches };
  return api;
});
